import React, { memo } from "react";
import salesicon from "@/assets/images/salesicon.png";
import { markdownToHtml } from "@/shared/utils/markdownUtils";
import { handleArtifactLinkClick } from "@/shared/utils/artifactDownload";
import CommonAvatar from "../Avatar/CommonAvatar";
import animatedLogo from "@/assets/images/animated_logo.gif";
import ReasoningSteps from "../PromptKit/ReasoningSteps";
import MessageContent from "../PromptKit/MessageContent";
import { buildMentionRegex, splitMentions } from "@/shared/utils/mentions";

const ChatMessage = memo(
  ({
    message,
    streamingMessageId,
    userImage,
    userName,
    showFollowUp = true,
    mentionNames = [],
    usePromptKitMarkdown = false,
  }) => {
    const isUser = message.role === "user";
    const isStreaming = message.id === streamingMessageId;
    // Follow-up options render in the input-bar panel (see BaseChat), not in
    // the bubble. Hide bubble text only while the question is active AND it
    // merely duplicates the question shown in that panel.
    const questionActive =
      showFollowUp &&
      Array.isArray(message.follow_up?.options) &&
      message.follow_up.options.length > 0;
    const visibleContent =
      questionActive &&
      message.content?.trim() === message.follow_up?.question?.trim()
        ? ""
        : message.content;
    // Reasoning trace (model-authored steps + live tool steps) from the backend.
    const thinkingSteps = Array.isArray(message.steps) ? message.steps : [];

    if (isUser) {
      return (
        <div
          key={message.id}
          className="relative w-fit max-w-[85%] ml-auto flex text-sm text-gray-800 mb-1"
        >
          <CommonAvatar
            imageUrl={userImage}
            name={userName}
            className="absolute top-0 h-8 w-8 right-0 flex-shrink-0"
            size="md"
          />
          <div
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            className="bg-gray-100 dark:bg-gray-800 w-full p-3 ml-auto mr-12 rounded-md text-sm text-gray-800 dark:text-gray-100 overflow-hidden"
          >
            {splitMentions(
              message.content || "",
              buildMentionRegex(mentionNames),
            ).map((part) =>
              typeof part === "string" ? (
                part
              ) : (
                // Tagged entity: the "@" is a typing affordance, not content.
                <span
                  key={`${part.index}-${part.name}`}
                  className="font-bold text-secondary"
                >
                  {part.name}
                </span>
              ),
            )}
          </div>
        </div>
      );
    }

    // Nothing to show (e.g. the question lives in the input-bar panel) — skip
    // the bubble entirely instead of leaving a floating avatar.
    if (!visibleContent && !isStreaming && !thinkingSteps.length) {
      return null;
    }

    return (
      <div
        key={message.id}
        className="relative flex w-full items-start text-sm text-gray-800 mb-1"
      >
        <img
          src={isStreaming ? animatedLogo : salesicon}
          alt="salesicon"
          className="absolute top-0 h-8 w-8 flex-shrink-0"
        />
        <div className="w-full ml-12 min-w-0">
          {thinkingSteps.length > 0 && (
            <ReasoningSteps
              steps={thinkingSteps}
              live={isStreaming}
              title="Reasoning"
              liveTitle="Thinking..."
            />
          )}
        {(visibleContent || (isStreaming && !thinkingSteps.length)) && (
          <div className="bg-gray-100 dark:bg-gray-800/90 w-full p-3 rounded-md text-sm text-gray-800 dark:text-gray-100 markdown-content overflow-hidden break-words prose dark:prose-invert prose-sm max-w-none [&_a]:text-secondary dark:[&_a]:text-secondary-light [&_a]:underline [&_a]:cursor-pointer">
            {usePromptKitMarkdown ? (
              <MessageContent markdown>{visibleContent}</MessageContent>
            ) : (
              <div
                style={{ wordBreak: "break-word" }}
                onClick={handleArtifactLinkClick}
                dangerouslySetInnerHTML={{
                  __html: visibleContent
                    ? markdownToHtml(visibleContent)
                    : '<span class="text-gray-400">...</span>',
                }}
              />
            )}
          </div>
        )}
        </div>
      </div>
    );
  },
);

export default ChatMessage;
